// src/components/ui/Modal.jsx
// Accessible modal / bottom-sheet primitive.
//
// presentation:
//   'responsive' (default) — bottom sheet below `sm`, centered dialog at `sm+`
//   'center'               — always centered
//   'sheet'                — always a bottom sheet
//
// Portals to document.body so `fixed` positioning can't be hijacked by an
// ancestor transform (the calendar had to do this by hand for the waitlist
// modal). React context still crosses portals. Header/body/footer markup
// matches the app's hand-rolled modal convention so migrating an existing
// modal is mostly deleting wrapper markup. Panel animation/max-height classes
// live in index.css (`.modal-*`).

import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

// Body scroll lock, refcounted so nested modals don't unlock early
let lockCount = 0;
let savedOverflow = '';
function lockBody() {
  if (lockCount++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
}
function unlockBody() {
  if (--lockCount <= 0) {
    lockCount = 0;
    document.body.style.overflow = savedOverflow;
  }
}

// Escape closes only the top-most open modal
const openStack = [];

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
};

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  headerLeft,
  presentation = 'responsive',
  size = 'lg',
  showClose = true,
  closeOnBackdrop = true,
  closeDisabled = false,
  initialFocusRef,
  className,
  bodyClassName,
}) {
  const reactId = useId();
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const pointerDownOnBackdrop = useRef(false);

  // Latest callbacks/flags for the once-registered key listener
  const latest = useRef({ onClose, closeDisabled });
  latest.current = { onClose, closeDisabled };
  const requestClose = () => {
    if (!latest.current.closeDisabled) latest.current.onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    restoreFocusRef.current = document.activeElement;
    openStack.push(reactId);
    lockBody();

    const onKeyDown = (e) => {
      if (e.key !== 'Escape' || openStack[openStack.length - 1] !== reactId) return;
      e.stopPropagation();
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const target =
      initialFocusRef?.current ||
      panelRef.current?.querySelector(FOCUSABLE) ||
      panelRef.current;
    target?.focus?.({ preventScroll: true });

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = openStack.lastIndexOf(reactId);
      if (i >= 0) openStack.splice(i, 1);
      unlockBody();
      restoreFocusRef.current?.focus?.({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const titleId = title ? `${reactId}-title` : undefined;
  const isSheet = presentation === 'sheet';
  const isCenter = presentation === 'center';
  const isResponsive = !isSheet && !isCenter;

  return createPortal(
    <div
      className={cn(
        'modal-backdrop fixed inset-0 z-50 bg-black/50 flex justify-center',
        isSheet && 'items-end',
        isCenter && 'items-center p-4',
        isResponsive && 'items-end sm:items-center sm:p-4'
      )}
      onMouseDown={(e) => {
        pointerDownOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (closeOnBackdrop && pointerDownOnBackdrop.current && e.target === e.currentTarget) {
          requestClose();
        }
        pointerDownOnBackdrop.current = false;
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'modal-panel bg-white shadow-xl w-full flex flex-col focus:outline-none',
          SIZES[size] || SIZES.lg,
          isSheet && 'modal-panel-sheet rounded-t-xl',
          isCenter && 'modal-panel-center rounded-lg',
          isResponsive && 'modal-panel-responsive rounded-t-xl sm:rounded-lg',
          className
        )}
      >
        {(title || headerLeft || showClose) && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            {headerLeft}
            {title && (
              <h2 id={titleId} className="text-lg font-semibold text-gray-900">
                {title}
              </h2>
            )}
            {showClose ? (
              <button
                type="button"
                onClick={requestClose}
                disabled={closeDisabled}
                aria-label="Close"
                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            ) : (
              <div className="w-9" aria-hidden="true" />
            )}
          </div>
        )}

        <div className={cn('flex-1 min-h-0 overflow-y-auto', bodyClassName)}>{children}</div>

        {footer && (
          <div className="px-4 py-4 border-t border-gray-200 flex-shrink-0">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
